import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ZOBOX_PRIMARY = '#2b3380';

// ==================== CREATE DOCUMENT FROM TEMPLATE MODAL ====================
export function CreateDocumentModal({ isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState(1); // 1: Select Template, 2: Fill Placeholders
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [documentData, setDocumentData] = useState({
    document_name: '',
    party_name: '',
    party_email: '',
    party_contact: '',
    effective_date: new Date().toISOString().split('T')[0],
    expiry_date: '',
    requires_signature: true,
    notes: ''
  });
  const [placeholders, setPlaceholders] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/legal/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || []);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setDocumentData(prev => ({
      ...prev,
      document_name: template.template_name
    }));
    // Initialize placeholders
    const initialPlaceholders = {};
    template.placeholders?.forEach(ph => {
      initialPlaceholders[ph] = '';
    });
    setPlaceholders(initialPlaceholders);
    setStep(2);
  };

  const handlePlaceholderChange = (key, value) => {
    setPlaceholders(prev => ({ ...prev, [key]: value }));
  };

  const handleCreate = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...documentData,
        template_id: selectedTemplate.id,
        placeholders
      };

      await axios.post(`${API}/legal/documents`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Document created successfully!');
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create document');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedTemplate(null);
    setDocumentData({
      document_name: '',
      party_name: '',
      party_email: '',
      party_contact: '',
      effective_date: new Date().toISOString().split('T')[0],
      expiry_date: '',
      requires_signature: true,
      notes: ''
    });
    setPlaceholders({});
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold" style={{ color: ZOBOX_PRIMARY }}>
              Create Document from Template
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Step {step} of 2: {step === 1 ? 'Select Template' : 'Fill Details'}
            </p>
          </div>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {/* Step 1: Template Selection */}
          {step === 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map(template => (
                <div
                  key={template.id}
                  onClick={() => handleTemplateSelect(template)}
                  className="border-2 border-gray-200 rounded-lg p-4 cursor-pointer hover:border-[#2b3380] transition"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-lg">{template.template_name}</h3>
                    <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded">
                      {template.category}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                  <div className="text-xs text-gray-500">
                    Version {template.version} • {template.placeholders?.length || 0} fields
                  </div>
                </div>
              ))}
              {templates.length === 0 && (
                <div className="col-span-2 text-center py-12 text-gray-500">
                  No templates available
                </div>
              )}
            </div>
          )}

          {/* Step 2: Fill Details */}
          {step === 2 && selectedTemplate && (
            <div className="space-y-6">
              {/* Document Info */}
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: ZOBOX_PRIMARY }}>
                  Document Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Document Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={documentData.document_name}
                      onChange={(e) => setDocumentData(prev => ({ ...prev, document_name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Party Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={documentData.party_name}
                      onChange={(e) => setDocumentData(prev => ({ ...prev, party_name: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Party Email
                    </label>
                    <input
                      type="email"
                      value={documentData.party_email}
                      onChange={(e) => setDocumentData(prev => ({ ...prev, party_email: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Party Contact
                    </label>
                    <input
                      type="tel"
                      value={documentData.party_contact}
                      onChange={(e) => setDocumentData(prev => ({ ...prev, party_contact: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Effective Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={documentData.effective_date}
                      onChange={(e) => setDocumentData(prev => ({ ...prev, effective_date: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expiry Date
                    </label>
                    <input
                      type="date"
                      value={documentData.expiry_date}
                      onChange={(e) => setDocumentData(prev => ({ ...prev, expiry_date: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={documentData.requires_signature}
                      onChange={(e) => setDocumentData(prev => ({ ...prev, requires_signature: e.target.checked }))}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Requires Digital Signature</span>
                  </label>
                </div>
              </div>

              {/* Template Placeholders */}
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ color: ZOBOX_PRIMARY }}>
                  Template Fields
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {selectedTemplate.placeholders?.map(placeholder => (
                    <div key={placeholder}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {placeholder.replace(/_/g, ' ')}
                      </label>
                      <input
                        type="text"
                        value={placeholders[placeholder] || ''}
                        onChange={(e) => handlePlaceholderChange(placeholder, e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
                        placeholder={`Enter ${placeholder.replace(/_/g, ' ')}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={documentData.notes}
                  onChange={(e) => setDocumentData(prev => ({ ...prev, notes: e.target.value }))}
                  rows="3"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
                  placeholder="Additional notes..."
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between gap-3 px-6 py-4 border-t">
          {step === 2 && (
            <button
              onClick={() => setStep(1)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              ← Back
            </button>
          )}
          <div className="flex-1"></div>
          <button
            onClick={handleClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          {step === 2 && (
            <button
              onClick={handleCreate}
              disabled={loading}
              className="px-6 py-2 text-white rounded-lg"
              style={{ backgroundColor: loading ? '#cccccc' : ZOBOX_PRIMARY }}
            >
              {loading ? 'Creating...' : 'Create Document'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== CREATE TEMPLATE MODAL ====================
export function CreateTemplateModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    template_name: '',
    category: 'General',
    description: '',
    template_content: '',
    placeholders: '',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        placeholders: formData.placeholders.split(',').map(p => p.trim()).filter(Boolean)
      };

      await axios.post(`${API}/legal/templates`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Template created successfully!');
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create template');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      template_name: '',
      category: 'General',
      description: '',
      template_content: '',
      placeholders: '',
      is_active: true
    });
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold" style={{ color: ZOBOX_PRIMARY }}>Create New Template</h2>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="template_name"
                value={formData.template_name}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
                placeholder="e.g., Employment Contract"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
              >
                <option value="General">General</option>
                <option value="Employment">Employment</option>
                <option value="Franchisee">Franchisee</option>
                <option value="NDA">NDA</option>
                <option value="Vendor">Vendor</option>
                <option value="Termination">Termination</option>
                <option value="Compliance">Compliance</option>
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="2"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
              placeholder="Brief description of this template"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Template Content <span className="text-red-500">*</span>
            </label>
            <textarea
              name="template_content"
              value={formData.template_content}
              onChange={handleChange}
              required
              rows="12"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380] font-mono text-sm"
              placeholder="Enter template content with placeholders like {{PLACEHOLDER_NAME}}"
            />
            <p className="text-xs text-gray-500 mt-1">
              Use double curly braces for placeholders: {`{{COMPANY_NAME}}, {{DATE}}, etc.`}
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Placeholders <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="placeholders"
              value={formData.placeholders}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
              placeholder="DATE, COMPANY_NAME, EMPLOYEE_NAME (comma-separated)"
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter placeholder names separated by commas (without {`{{}}`})
            </p>
          </div>

          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Active Template</span>
            </label>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 text-white rounded-lg"
              style={{ backgroundColor: loading ? '#cccccc' : ZOBOX_PRIMARY }}
            >
              {loading ? 'Creating...' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==================== CREATE CASE MODAL ====================
export function CreateCaseModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    case_title: '',
    case_type: 'Dispute',
    description: '',
    party_involved: '',
    priority: 'Medium',
    incident_date: new Date().toISOString().split('T')[0],
    due_date: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/legal/cases`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Case created successfully!');
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create case');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      case_title: '',
      case_type: 'Dispute',
      description: '',
      party_involved: '',
      priority: 'Medium',
      incident_date: new Date().toISOString().split('T')[0],
      due_date: ''
    });
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold" style={{ color: ZOBOX_PRIMARY }}>Create New Case</h2>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Case Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="case_title"
                value={formData.case_title}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
                placeholder="Brief title for the case"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Case Type <span className="text-red-500">*</span>
              </label>
              <select
                name="case_type"
                value={formData.case_type}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
              >
                <option value="Dispute">Dispute</option>
                <option value="Payment Dispute">Payment Dispute</option>
                <option value="Compliance Issue">Compliance Issue</option>
                <option value="Contract Review">Contract Review</option>
                <option value="Investigation">Investigation</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority <span className="text-red-500">*</span>
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Party Involved
              </label>
              <input
                type="text"
                name="party_involved"
                value={formData.party_involved}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
                placeholder="Name of party involved"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Incident Date
              </label>
              <input
                type="date"
                name="incident_date"
                value={formData.incident_date}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                name="due_date"
                value={formData.due_date}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                rows="4"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#2b3380]"
                placeholder="Detailed description of the case..."
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 text-white rounded-lg"
              style={{ backgroundColor: loading ? '#cccccc' : ZOBOX_PRIMARY }}
            >
              {loading ? 'Creating...' : 'Create Case'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
